import type { ReactNode } from 'react'
import { createBrowserRouter, RouterProvider, Navigate, useParams } from 'react-router-dom'
import { Layout } from '@/components/layout/Layout'
import { RequireAuth } from '@/components/auth/RequireAuth'
import { Protected } from '@/components/auth/Protected'
import { FeatureGated } from '@/components/auth/FeatureGated'
import { PERMISSIONS as P } from '@/lib/permissions'
import { FEATURES as F, type FeatureKey } from '@/lib/featureFlags'
import { LoginPage } from '@/pages/LoginPage'
import { ResetPasswordPage } from '@/pages/ResetPasswordPage'
import { SsoStartPage } from '@/pages/SsoStartPage'
import { SsoCallbackPage } from '@/pages/SsoCallbackPage'
import { AccessDeniedPage } from '@/pages/AccessDeniedPage'
import { ConfigurePage } from '@/pages/ConfigurePage'
import { TenancySetupPage } from '@/pages/TenancySetupPage'
import { ProfilePage } from '@/pages/ProfilePage'
import { DashboardPage } from '@/pages/DashboardPage'
import { DashboardsPage } from '@/pages/DashboardsPage'
import { ShiftDashboardPage } from '@/pages/ShiftDashboardPage'
import { MyTasksPage } from '@/pages/MyTasksPage'
import { SupportPage } from '@/pages/SupportPage'
import { MyProcessStepsPage } from '@/pages/MyProcessStepsPage'
import { ComponentsPage } from '@/pages/ComponentsPage'
import { FormBuilderPage } from '@/pages/FormBuilderPage'
import { FormEditorPage } from '@/pages/FormEditorPage'
import { ProceduresPage } from '@/pages/ProceduresPage'
import { UpcomingProceduresPage } from '@/pages/UpcomingProceduresPage'
import { OverallStatusPage } from '@/pages/OverallStatusPage'
import { InventoryPage } from '@/pages/InventoryPage'
import { ProductsPage } from '@/pages/ProductsPage'
import { ProductDetailPage } from '@/pages/ProductDetailPage'
import { StorageNodeRedirectPage } from '@/pages/StorageNodeRedirectPage'
import { StorageUnitView } from '@/pages/StorageUnitView'
import { StorageTemperaturesPage } from '@/pages/StorageTemperaturesPage'
import { StorageTransferPage } from '@/pages/StorageTransferPage'
import { StorageTransfersPage } from '@/pages/StorageTransfersPage'
import PickListsPage from '@/pages/PickListsPage'
import PickListPage from '@/pages/PickListPage'
import KitBuildRedirect from '@/pages/KitBuildRedirect'
import { PurchaseOrderPrintPage } from '@/pages/PurchaseOrderPrintPage'
import { StorageTransferPrintPage } from '@/pages/StorageTransferPrintPage'
import { PatientSummaryPrintPage } from '@/pages/PatientSummaryPrintPage'
import KitDetailPage from '@/pages/KitDetailPage'
import { ReportsPage } from '@/pages/ReportsPage'
import { NotificationsPage } from '@/pages/NotificationsPage'
import { SettingsPage } from '@/pages/SettingsPage'
import { IntegrationsPage } from '@/pages/IntegrationsPage'
import { IntegrationFormPage } from '@/pages/IntegrationFormPage'
import { IntegrationDetailPage } from '@/pages/IntegrationDetailPage'
import { LisReviewQueuePage } from '@/pages/LisReviewQueuePage'

import { CollectionPage } from '@/pages/CollectionPage'
import { FormRendererPage } from '@/pages/FormRendererPage'
import { FormDefinitionsPage } from '@/pages/FormDefinitionsPage'
import { AllSubmissionsPage } from '@/pages/AllSubmissionsPage'
import { PatientsOverviewPage } from '@/pages/PatientsOverviewPage'
import { PatientCreatePage } from '@/pages/PatientCreatePage'
import { PatientJourneyPage } from '@/pages/PatientJourneyPage'
import { PatientProcedurePage } from '@/pages/PatientProcedurePage'
import { ActionItemsPage } from '@/pages/ActionItemsPage'
import { ProcedureTemplatesPage } from '@/pages/ProcedureTemplatesPage'
import { SequenceEditorPage } from '@/pages/SequenceEditorPage'
import { EquipmentPlannerPage } from '@/pages/EquipmentPlannerPage'
import { ResourcesPage } from '@/pages/ResourcesPage'
import { ResourceProfilePage } from '@/pages/ResourceProfilePage'
import { HelpPage } from '@/pages/HelpPage'
import { AuditTrailCenterPage } from '@/pages/AuditTrailCenterPage'
import { AUDIT_CENTER_READ_PERMISSIONS } from '@/lib/auditCenter'

// /help was renamed to /wiki — keep old bookmarks/links working by forwarding
// to the same page under the new path, preserving any :section/:slug params.
function WikiLegacyRedirect() {
  const { section, slug } = useParams<{ section?: string; slug?: string }>()
  const to = section && slug ? `/wiki/${section}/${slug}` : section ? `/wiki/${section}` : '/wiki'
  return <Navigate to={to} replace />
}

// Wrap a route element with permission protection (redirects to /no-access).
const guard = (anyOf: readonly string[], element: ReactNode): ReactNode => (
  <Protected anyOf={anyOf}>{element}</Protected>
)

// Wrap a route element with a per-tenant feature gate (also /no-access). The
// list is the route's HARD dependencies only — features it merely uses for a
// widget degrade in place instead (see FeatureGated's docblock).
const featureGuard = (features: readonly FeatureKey[], element: ReactNode): ReactNode => (
  <FeatureGated features={features}>{element}</FeatureGated>
)

/**
 * Both gates, composed PERMISSION FIRST then feature — `Protected` outermost, so
 * a user who may not see the screen at all is refused without the app first
 * disclosing, via a different redirect timing, whether the tenant has the
 * feature. Both denials land on /no-access, so the order is invisible in the UI
 * and only matters for that.
 */
const featureRoute = (
  features: readonly FeatureKey[],
  anyOf: readonly string[],
  element: ReactNode,
): ReactNode => guard(anyOf, featureGuard(features, element))

const layoutChildren = [
  // The root path is a pure redirect — there is no landing page. This route sits
  // inside RequireAuth, so an unauthenticated visitor is bounced to /login first
  // and only an authenticated one reaches the dashboard.
  { path: '/', element: <Navigate to='/dashboard' replace /> },

  // Always available to any authenticated user.
  { path: '/profile', element: <ProfilePage /> },

  { path: '/dashboard', element: guard([P.dashboardView], <DashboardPage />) },
  // The user's own named dashboards (z8nrz7eqyu), behind the Home menu's
  // Dashboards tile. /dashboard (singular) stays Home: login lands there.
  { path: '/dashboards', element: guard([P.dashboardView], <DashboardsPage />) },
  // Its own page, reached from the header beside Audit (z8nrz7fbcj).
  {
    path: '/shift-dashboard',
    element: guard([P.shiftDashboardView, P.shiftDashboardManage], <ShiftDashboardPage />),
  },
  // My Tasks (z8nrz7fh6h), reached from the header beside Audit. The server
  // decides whether it shows your own tasks or every resource.
  {
    path: '/my-tasks',
    element: guard([P.myTasksRead, P.resourceCalendarReadAll], <MyTasksPage />),
  },
  // Title21 Support (Smart Support): report an issue and follow it. The
  // :number deep link is what the SUPPORT_TICKET bell notification opens.
  { path: '/support', element: featureRoute([F.support], [P.supportRead], <SupportPage />) },
  { path: '/support/:number', element: featureRoute([F.support], [P.supportRead], <SupportPage />) },
  { path: '/components', element: guard([P.settingsView], <ComponentsPage />) },

  // The form engine. Its inner tabs (field library, procedure templates) carry
  // their own SOFT gates inside FormBuilderPage — losing one tab must not take
  // the whole builder down with it.
  {
    path: '/form-builder',
    element: featureRoute([F.formEngine], [P.formsCreate, P.formsEdit], <FormBuilderPage />),
  },
  {
    path: '/form-builder/new',
    element: featureRoute([F.formEngine], [P.formsCreate], <FormEditorPage />),
  },
  {
    path: '/form-builder/:id',
    element: featureRoute([F.formEngine], [P.formsCreate, P.formsEdit], <FormEditorPage />),
  },
  {
    path: '/form-definitions',
    element: featureRoute([F.formEngine], [P.formsRead], <FormDefinitionsPage />),
  },

  {
    path: '/procedures',
    element: featureRoute([F.procedures], [P.proceduresRead], <ProceduresPage />),
  },
  {
    path: '/procedures/calendar',
    element: featureRoute([F.procedures], [P.proceduresRead], <UpcomingProceduresPage />),
  },
  {
    path: '/board',
    element: featureRoute([F.procedures], [P.proceduresRead], <OverallStatusPage />),
  },

  { path: '/inventory', element: featureRoute([F.inventory], [P.inventoryView], <InventoryPage />) },
  {
    path: '/inventory/pick-lists',
    element: featureRoute([F.inventory], [P.inventoryView], <PickListsPage />),
  },
  // One storage unit, map first. The same page as /equipment/storage/:unitId,
  // each under its own guard — no redirect between them.
  {
    path: '/inventory/storage/:unitId',
    element: featureRoute([F.inventory], [P.inventoryView], <StorageUnitView surface='inventory' />),
  },
  // The Temperatures screen (❄ Temperatures on the storage list). Its own
  // segment, so it can never be read as a ':unitId'.
  {
    path: '/inventory/storage-temperatures',
    element: featureRoute([F.inventory], [P.inventoryView], <StorageTemperaturesPage />),
  },
  // A node id resolved to the unit that can display it. Four segments against
  // ':unitId''s three, so the two never compete for a URL.
  {
    path: '/inventory/storage/node/:nodeId',
    element: featureRoute([F.inventory], [P.inventoryView], <StorageNodeRedirectPage />),
  },
  // A storage unit's task procedure run (z8nrz7fjzc), opened from Inventory's
  // Tasks tab: the Cleaning / Equipment run page, its trail leading back there.
  {
    path: '/inventory/runs/:procedureId',
    element: featureGuard([F.inventory, F.equipment], <PatientProcedurePage variant='task' />),
  },
  // Planning a swap is gated apart from viewing storage: the person approving
  // one and the tech who will execute it are not the same person.
  //
  // The LIST is why an approved plan can be found again at all — without it
  // the only route to one was the redirect that created it, so closing the tab
  // lost a plan that was freezing a freezer.
  {
    path: '/inventory/storage-transfers',
    element: featureRoute([F.inventory], [P.storageReplace], <StorageTransfersPage />),
  },
  {
    path: '/inventory/storage-transfers/:transferId',
    element: featureRoute([F.inventory], [P.storageReplace], <StorageTransferPage />),
  },
  // Kits is a first-class page beside Procedures, not an Inventory tab: kitting
  // is manufacturing, not patient care. Inventory keeps its own Kits tab, which
  // shows kits as STOCK alongside consumables and reagents.
  // The Kits list IS the Procedures table in kit variant — same expand rows,
  // progress, status, assignee, dates and pagination, identical by
  // construction rather than by reimplementation.
  // The Cleaning module (z8nrz7ewt3): the Equipment page listing only rooms —
  // a room is equipment with purpose ROOM — and calling them rooms. Gated
  // exactly like /equipment, since it is the same page and the same API. Keyed
  // apart, so moving between the two remounts the page rather than carrying a
  // tab or filter over from the other one.
  { path: '/cleaning', element: featureGuard([F.equipment], <EquipmentPlannerPage key='room' variant='room' />) },
  // A cleaning / maintenance task's procedure run (z8nrz7ewt1), opened from
  // Pending Actions: the same procedure page with no patient, its trail leading
  // back to the page it came from.
  { path: '/cleaning/runs/:procedureId', element: featureGuard([F.equipment], <PatientProcedurePage variant='task' />) },
  { path: '/equipment/runs/:procedureId', element: featureGuard([F.equipment], <PatientProcedurePage variant='task' />) },
  // An MES order renders through the SAME procedure page as a transplant and a
  // kit — third variant, not a third clone (z8nrz7ckj9). Its first column is the
  // minted MFG number where the kit variant shows a supply id.
  {
    path: '/mes',
    element: <ProceduresPage variant='mes' />,
  },
  // An MES order opens through the SAME procedure page as a transplant and a
  // kit, with the patient half switched off entirely — a kit at least carries
  // its supply id in patientMrn, an MES order has nothing (z8nrz7ckj9). The
  // journey is scoped by procedure id alone, which the server accepts.
  {
    path: '/mes/orders/:procedureId',
    element: <PatientProcedurePage variant='mes' />,
  },
  {
    path: '/kits',
    element: featureRoute([F.inventory], [P.inventoryView], <ProceduresPage variant='kit' />),
  },
  // A kit run renders through the SAME procedure page as a transplant — same
  // steps, forms, process step, assign and initiate — with the patient half
  // switched off. Not a clone: one component, two variants.
  {
    path: '/kits/runs/:procedureId',
    element: featureRoute(
      [F.inventory],
      [P.inventoryView],
      <PatientProcedurePage variant='kit' />,
    ),
  },
  {
    // A finished kit as a RECORD, not as work. Reached only from
    // Inventory → Kits → Kit Inventory; the kit run at /kits/runs/:id is a
    // different question and stays where it is.
    path: '/inventory/kits/completed/:buildId',
    element: featureRoute([F.inventory], [P.inventoryView], <KitDetailPage />),
  },
  {
    // Kept for one release: the router's catch-all silently bounces unknown
    // paths to the dashboard, so deleting this outright would make every
    // bookmarked build link fail with no explanation.
    path: '/inventory/kits/builds/:id',
    element: featureRoute([F.inventory], [P.inventoryView], <KitBuildRedirect />),
  },
  {
    path: '/inventory/pick-lists/:id',
    element: featureRoute([F.inventory], [P.inventoryView], <PickListPage />),
  },
  // The CAPA page is the procedures table in its `capa` variant: every
  // procedure that is CAPA-typed or carries a form of category `capa`. Same
  // component as Transplants / Kits / MES, so the columns, paging and filters
  // cannot drift apart.
  // No detail route of its own: a CAPA is a FORM inside a run, so a CAPA row
  // opens the page of whichever run holds it — a transplant's, a kit build's, an
  // MES order's — focused on that form. See `procedureRunPath`.
  { path: '/capa', element: guard([P.capaView], <ProceduresPage variant='capa' />) },
  // Follow-ups raised on forms and records (z8nrz7fkfh).
  { path: '/action-items', element: guard([P.actionItemsView], <ActionItemsPage />) },
  {
    path: '/my-steps',
    // Reviewers reach their review worklist here too (z8nrz7dt41), so either
    // permission opens the page; each section checks its own.
    element: featureRoute(
      [F.procedureTemplates],
      [P.processExecute, P.submissionsReview],
      <MyProcessStepsPage />,
    ),
  },
  // One route with optional params, not three: the topic/report selection is
  // read from `useParams` inside the page, so the `reports.view` guard is
  // declared once instead of being restated per sub-route.
  { path: '/reports/:topic?/:report?', element: guard([P.reportsView], <ReportsPage />) },
  // Notifications: available to any authenticated user (no permission gate).
  { path: '/notifications', element: <NotificationsPage /> },
  { path: '/alerts', element: <Navigate to='/notifications' replace /> },
  { path: '/settings', element: guard([P.settingsView], <SettingsPage />) },

  // User & role management live in the Configure page's tabs (matches staging).
  {
    path: '/configure',
    element: guard([P.usersManage, P.rolesRead, P.rolesManage], <ConfigurePage />),
  },
  // TEMPORARY adoption tooling — linked from Configure > Networks & Tenants,
  // deleted with the backend tenancy-setup module once adoption is complete.
  { path: '/tenancy-setup', element: guard([P.tenantsManage], <TenancySetupPage />) },
  { path: '/administration', element: <Navigate to='/configure?tab=users' replace /> },
  { path: '/users', element: <Navigate to='/configure?tab=users' replace /> },
  // The address given to hospital admins; the page is Configure's SSO tab.
  { path: '/sso-setup', element: <Navigate to='/configure?tab=sso' replace /> },
  { path: '/roles', element: <Navigate to='/configure?tab=roles' replace /> },
  { path: '/admins', element: <Navigate to='/configure?tab=users' replace /> },

  {
    path: '/integrations',
    element: featureRoute([F.integrations], [P.integrationsRead], <IntegrationsPage />),
  },
  {
    path: '/integrations/new',
    element: featureRoute([F.integrations], [P.integrationsManage], <IntegrationFormPage />),
  },
  // Not linked from any menu (z8nrz7fcu4): the whole LIS review queue at once.
  // Gated on what its endpoint needs, not on integrations.read.
  {
    path: '/integrations/lis-review',
    element: featureRoute([F.integrations], [P.submissionsRead], <LisReviewQueuePage />),
  },
  {
    path: '/integrations/:id',
    element: featureRoute([F.integrations], [P.integrationsRead], <IntegrationDetailPage />),
  },

  {
    path: '/patients',
    element: featureRoute([F.patients], [P.submissionsRead], <PatientsOverviewPage />),
  },
  // Listed before /patients/:mrn for readability; React Router ranks static segments
  // above dynamic ones regardless, so 'new' is never swallowed as an MRN.
  {
    path: '/patients/new',
    element: featureRoute([F.patients], [P.proceduresManage], <PatientCreatePage />),
  },
  {
    path: '/patients/:mrn',
    element: featureRoute([F.patients], [P.submissionsRead], <PatientJourneyPage />),
  },
  {
    path: '/patients/:mrn/procedures/:procedureId',
    element: featureRoute([F.patients], [P.submissionsRead], <PatientProcedurePage />),
  },

  {
    path: '/procedure-templates',
    element: featureRoute(
      [F.procedureTemplates],
      [P.procedureTemplatesRead],
      <ProcedureTemplatesPage />,
    ),
  },
  // Hard on BOTH: the sequence editor wires form definitions into a procedure
  // DAG, so it is meaningless without either side.
  {
    path: '/procedure-templates/:id/sequence',
    element: featureRoute(
      [F.procedureTemplates, F.formEngine],
      [P.procedureTemplatesManage],
      <SequenceEditorPage />,
    ),
  },

  // Soft deps deliberately absent: the renderer also reads patients (MR fields),
  // equipment (process-step pickers) and procedures (journey gating), each of
  // which degrades in place rather than blanking a clinical form.
  {
    path: '/forms',
    element: featureRoute([F.formEngine], [P.submissionsRead], <CollectionPage />),
  },
  {
    path: '/forms/:submissionId',
    element: featureRoute([F.formEngine], [P.submissionsRead], <FormRendererPage />),
  },
  {
    path: '/submissions',
    element: featureRoute([F.formEngine], [P.submissionsRead], <AllSubmissionsPage />),
  },

  // Feature-gated but NOT permission-gated: this route has never carried a
  // permission, and adding one here would silently lock out users who can reach
  // it today. The backend equipment routes are the authority on access.
  { path: '/equipment', element: featureGuard([F.equipment], <EquipmentPlannerPage key='equipment' />) },
  // The same unit page as /inventory/storage/:unitId. Feature-gated only,
  // matching /equipment above; the page hides its inventory-only parts.
  {
    path: '/equipment/storage/:unitId',
    element: featureGuard([F.equipment], <StorageUnitView surface='equipment' />),
  },
  // Its own page showing storage only. It used to redirect to
  // `/inventory?tab=products`, a tab the inventory rebuild removed, so it landed
  // on Consumables instead. Gated like the rest of the module.
  {
    path: '/products',
    element: featureRoute([F.inventory], [P.inventoryView], <ProductsPage />),
  },
  // What an audit row's DIN, product code and `product` link point at. Guarded
  // exactly like the list above it, so a reader who can reach one can reach the
  // other.
  {
    path: '/products/:id',
    element: featureRoute([F.inventory], [P.inventoryView], <ProductDetailPage />),
  },
  { path: '/resources', element: <ResourcesPage /> },
  { path: '/resources/people/:userId', element: <ResourceProfilePage /> },

  // The Audit Trail Center (z8nrz7dq6q), reached from "Audit" in the header.
  // Any audit key opens it; the page shows only the trails the role may read.
  { path: '/audit', element: guard(AUDIT_CENTER_READ_PERMISSIONS, <AuditTrailCenterPage />) },
  { path: '/wiki', element: <HelpPage /> },
  { path: '/wiki/:section', element: <HelpPage /> },
  { path: '/wiki/:section/:slug', element: <HelpPage /> },
  // Old path, kept working for existing bookmarks/links.
  { path: '/help', element: <WikiLegacyRedirect /> },
  { path: '/help/:section', element: <WikiLegacyRedirect /> },
  { path: '/help/:section/:slug', element: <WikiLegacyRedirect /> },
]

const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  { path: '/sso', element: <SsoStartPage /> },
  { path: '/auth/callback', element: <SsoCallbackPage /> },
  { path: '/reset-password', element: <ResetPasswordPage /> },
  { path: '/no-access', element: <AccessDeniedPage /> },
  {
    element: <RequireAuth />,
    children: [
      { element: <Layout />, children: layoutChildren },
      // Printable documents: authenticated, but without the app chrome, so
      // the page is the document alone (z8nrz7f7dz).
      {
        path: '/inventory/purchase-orders/:id/print',
        element: guard([P.purchaseOrdersRead], <PurchaseOrderPrintPage />),
      },
      // Same gate as the plan page itself (featureRoute, not a narrower one):
      // the brief is "anyone who can see the page" may print it, nothing more.
      {
        path: '/inventory/storage-transfers/:transferId/print',
        element: featureRoute([F.inventory], [P.storageReplace], <StorageTransferPrintPage />),
      },
      // The patient's full record as a printable summary (z8nrz7g57g). Same gate
      // as the patient page it is printed from.
      {
        path: '/patients/:mrn/print',
        element: featureRoute([F.patients], [P.submissionsRead], <PatientSummaryPrintPage />),
      },
    ],
  },
  { path: '*', element: <Navigate to='/' replace /> },
])

export function AppRouter() {
  return <RouterProvider router={router} />
}
